/**
 * AssistantSheet — Mobiler KI-Assistent (Vollbild-Chat)
 *
 * Master-Router + Doppel-Leiste. Kopf-Buttons: Code (Dev-Status), Posteingang (Vorschläge),
 * Schild (Sicherheit/Aktionen). Read-only im Chat; Schreiben nur per TOTP-Bestätigung.
 */

import { useEffect, useRef, useState } from 'react';
import { X, Send, Sparkles, Mic, ShieldCheck, Inbox, Code2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import AssistantSecurityPanel from './AssistantSecurityPanel';
import AssistantInboxPanel from './AssistantInboxPanel';
import AssistantDevPanel from './AssistantDevPanel';

type Msg = { role: 'user' | 'assistant'; content: string };

type Sub = { id: string; label: string };
const HAUPTBEREICHE: { key: string; label: string; agents: Sub[] }[] = [
  { key: 'Auto', label: 'Auto (Master)', agents: [] },
  { key: 'SV-Büro', label: 'SV-Büro', agents: [{ id: 'sv-buero', label: 'Gutachten' }, { id: 'stellungnahme-assistent', label: 'Stellungnahmen' }, { id: 'werkstatt-agent', label: 'Werkstatt' }, { id: 'fahrzeugmanager', label: 'Fahrzeuge' }] },
  { key: 'Mietwagen', label: 'Mietwagen', agents: [{ id: 'fahrzeugmanager', label: 'Flotte' }, { id: 'executor', label: 'Ausführung' }] },
  { key: 'Autoglas', label: 'Autoglas', agents: [{ id: 'executor', label: 'Aufträge' }] },
  { key: 'Finanzen', label: 'Finanzen', agents: [{ id: 'buchhaltung', label: 'Buchhaltung' }, { id: 'finanz-agent', label: 'Forderungen' }, { id: 'steuern', label: 'Steuern' }, { id: 'controller', label: 'Controlling' }] },
  { key: 'Kommunikation', label: 'Kommunikation', agents: [{ id: 'email-agent', label: 'E-Mail' }, { id: 'email-hygiene', label: 'Hygiene' }, { id: 'crm-agent', label: 'CRM' }] },
  { key: 'Immobilien', label: 'Immobilien', agents: [{ id: 'immobilien', label: 'Objekte' }] },
  { key: 'Marketing', label: 'Marketing', agents: [{ id: 'marketing-agent', label: 'Social / Unfallgiganten' }] },
  { key: 'Recht & HR', label: 'Recht & HR', agents: [{ id: 'recht', label: 'Recht' }, { id: 'hr', label: 'HR' }] },
  { key: 'Entwicklung', label: 'Entwicklung', agents: [{ id: 'manus-dev', label: 'Dev' }] },
];

const VORSCHLAEGE = [
  'Was läuft gerade?',
  'Stellungnahme vorbereiten',
  'Homepage: Online-Terminbuchung bauen',
  'Mietwagen-Daten prüfen',
];

const BEGRUESSUNG: Msg = {
  role: 'assistant',
  content:
    'Hallo Andreas 👋 Ich bin dein Koop-Assistent (Master). Ich leite deine Frage automatisch an den richtigen Fach-Agenten weiter — oder wähl oben einen Bereich. Leg los!',
};

export default function AssistantSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [messages, setMessages] = useState<Msg[]>([BEGRUESSUNG]);
  const [input, setInput] = useState('');
  const [haupt, setHaupt] = useState('Auto');
  const [activeAgent, setActiveAgent] = useState<Sub | null>(null);
  const [lastAgent, setLastAgent] = useState<{ name: string; farbe?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [showDev, setShowDev] = useState(false);
  const [pendingAction, setPendingAction] = useState<any>(null);
  const [actionCode, setActionCode] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function send(text?: string) {
    const content = (text ?? input).trim();
    if (!content || loading) return;

    const next: Msg[] = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setLoading(true);

    const apiMessages = next.filter((m, i) => !(i === 0 && m.role === 'assistant'));

    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) {
        setMessages((m) => [...m, { role: 'assistant', content: 'Bitte melde dich neu an — deine Sitzung ist abgelaufen.' }]);
        setLoading(false);
        return;
      }
      const res = await fetch('/api/assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ bereich: haupt, agentId: activeAgent?.id || '', messages: apiMessages }),
      });
      const data = await res.json().catch(() => ({}));
      const reply: string =
        data?.reply || data?.error || 'Es gab ein Problem bei der Antwort. Bitte versuch es erneut.';
      if (data?.agent?.name) setLastAgent({ name: data.agent.name, farbe: data.agent.farbe });
      const agentPrefix = data?.agent?.name ? `[${data.agent.name}] ` : '';
      setMessages((m) => [...m, { role: 'assistant', content: agentPrefix + reply }]);
      if (data?.proposedAction?.action) {
        setPendingAction(data.proposedAction);
        setActionCode('');
        setActionResult(null);
      }
    } catch {
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: 'Verbindung fehlgeschlagen. Bist du online? Bitte erneut versuchen.' },
      ]);
    } finally {
      setLoading(false);
    }
  }

  async function runAction() {
    if (!pendingAction || actionBusy) return;
    setActionBusy(true);
    setActionResult(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch('/api/assistant-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
        body: JSON.stringify({ action: pendingAction.action, code: actionCode, params: pendingAction.params }),
      });
      const d = await res.json().catch(() => ({}));
      if (d?.ok) {
        setMessages((m) => [...m, { role: 'assistant', content: '✓ ' + (d.ergebnis || 'Aktion ausgeführt.') }]);
        setPendingAction(null);
        setActionCode('');
        setActionResult(null);
      } else {
        setActionResult(d?.error || 'Aktion fehlgeschlagen.');
      }
    } catch {
      setActionResult('Verbindung fehlgeschlagen.');
    } finally {
      setActionBusy(false);
    }
  }

  if (!open) return null;

  return (
    <>
    <div className="md:hidden fixed inset-0 z-[100] flex flex-col bg-gradient-to-b from-[#0e1430] via-[#0a0f22] to-[#070a16] text-slate-100">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-[calc(env(safe-area-inset-top)+14px)] pb-3 border-b border-white/10">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#6d8bff] to-[#9d6bff] flex items-center justify-center shadow-lg shadow-[#6d8bff]/40">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">Koop Assistent</p>
          <p className="text-[11px] leading-tight" style={{ color: lastAgent?.farbe || '#34d399' }}>
            ● {lastAgent ? lastAgent.name : 'Master · leitet weiter'}
          </p>
        </div>
        <button
          onClick={() => setShowDev(true)}
          aria-label="Code / Entwicklung"
          className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#a9bcff] active:bg-white/10"
        >
          <Code2 className="w-5 h-5" />
        </button>
        <button
          onClick={() => setShowInbox(true)}
          aria-label="Posteingang"
          className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#6d8bff] active:bg-white/10"
        >
          <Inbox className="w-5 h-5" />
        </button>
        <button
          onClick={() => setShowSecurity(true)}
          aria-label="Sicherheit & Aktionen"
          className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-emerald-400 active:bg-white/10"
        >
          <ShieldCheck className="w-5 h-5" />
        </button>
        <button
          onClick={onClose}
          aria-label="Schließen"
          className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 active:bg-white/10"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Doppel-Leiste: Hauptbereiche (oben) + Unter-Agenten (unten) */}
      <div className="border-b border-white/5">
        <div className="flex gap-2 overflow-x-auto px-4 pt-3 pb-1.5 no-scrollbar">
          {HAUPTBEREICHE.map((h) => (
            <button
              key={h.key}
              onClick={() => { setHaupt(h.key); setActiveAgent(null); }}
              className={`flex-none text-[12px] px-3 py-1.5 rounded-full border transition-colors ${
                haupt === h.key ? 'text-white border-transparent bg-gradient-to-br from-[#6d8bff] to-[#9d6bff]' : 'text-slate-300 border-white/15 bg-white/5'
              }`}
            >
              {h.label}
            </button>
          ))}
        </div>
        {(HAUPTBEREICHE.find((h) => h.key === haupt)?.agents.length ?? 0) > 0 && (
          <div className="flex gap-2 overflow-x-auto px-4 pb-2.5 no-scrollbar">
            {HAUPTBEREICHE.find((h) => h.key === haupt)!.agents.map((a) => {
              const on = activeAgent?.id === a.id && activeAgent?.label === a.label;
              return (
                <button
                  key={a.id + a.label}
                  onClick={() => setActiveAgent(on ? null : a)}
                  className={`flex-none text-[11px] px-2.5 py-1 rounded-lg border transition-colors ${
                    on ? 'text-white border-transparent bg-[#9d6bff]/70' : 'text-slate-400 border-white/10 bg-white/[0.03]'
                  }`}
                >
                  {a.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Nachrichten */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap ${
              m.role === 'user'
                ? 'ml-auto bg-gradient-to-br from-[#6d8bff] to-[#9d6bff] text-white rounded-br-md'
                : 'mr-auto bg-white/[0.07] border border-white/10 rounded-bl-md'
            }`}
          >
            {m.content}
          </div>
        ))}
        {loading && (
          <div className="mr-auto text-slate-400 text-[12px] px-2">Assistent denkt …</div>
        )}
        <div ref={endRef} />
      </div>

      {/* Vorschläge (nur am Anfang) */}
      {messages.length <= 1 && (
        <div className="flex gap-2 overflow-x-auto px-3 pb-2 no-scrollbar">
          {VORSCHLAEGE.map((v) => (
            <button
              key={v}
              onClick={() => send(v)}
              className="flex-none text-[12px] px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-slate-100 active:bg-white/10"
            >
              {v}
            </button>
          ))}
        </div>
      )}

      {/* Bestätigungs-Karte für vorgeschlagene Aktion */}
      {pendingAction && (
        <div className="mx-3 mb-2 rounded-2xl border border-[#9d6bff]/40 bg-[#9d6bff]/10 p-3 space-y-2">
          <p className="text-[12px] text-slate-200"><span className="font-semibold text-[#c3b4ff]">Bestätigung nötig:</span> {pendingAction.summary}</p>
          <input
            value={actionCode}
            onChange={(e) => setActionCode(e.target.value)}
            inputMode="numeric"
            placeholder="6-stelliger Code aus Authenticator"
            className="w-full bg-white/[0.07] border border-white/10 rounded-lg px-3 py-2 text-[13px] text-slate-100 placeholder:text-slate-500 outline-none"
          />
          <div className="flex gap-2">
            <button disabled={actionBusy} onClick={runAction} className="flex-1 py-2 rounded-lg bg-gradient-to-br from-[#6d8bff] to-[#9d6bff] text-white text-[13px] disabled:opacity-50">
              {actionBusy ? 'Wird ausgeführt …' : 'Bestätigen & ausführen'}
            </button>
            <button onClick={() => { setPendingAction(null); setActionResult(null); }} className="px-3 py-2 rounded-lg border border-white/15 text-slate-300 text-[13px]">Verwerfen</button>
          </div>
          {actionResult && <p className="text-[12px] text-amber-200">{actionResult}</p>}
          <p className="text-[10px] text-slate-500">Erst einrichten? Öffne oben das Schild-Symbol.</p>
        </div>
      )}

      {/* Eingabe */}
      <div className="flex items-center gap-2 px-3 pt-2 pb-[calc(env(safe-area-inset-bottom)+12px)] border-t border-white/10">
        <div className="flex-1 flex items-center bg-white/[0.07] border border-white/10 rounded-full px-4">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') send();
            }}
            placeholder="Frag etwas oder gib einen Auftrag…"
            className="flex-1 bg-transparent outline-none py-2.5 text-[14px] text-slate-100 placeholder:text-slate-500"
          />
        </div>
        <button
          type="button"
          disabled
          title="Sprache bald verfügbar"
          className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-500 cursor-not-allowed"
        >
          <Mic className="w-5 h-5" />
        </button>
        <button
          onClick={() => send()}
          disabled={loading || !input.trim()}
          aria-label="Senden"
          className="w-11 h-11 rounded-full bg-gradient-to-br from-[#6d8bff] to-[#9d6bff] flex items-center justify-center text-white disabled:opacity-40"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
    <AssistantSecurityPanel open={showSecurity} onClose={() => setShowSecurity(false)} />
    <AssistantInboxPanel open={showInbox} onClose={() => setShowInbox(false)} />
    <AssistantDevPanel open={showDev} onClose={() => setShowDev(false)} />
    </>
  );
}
